import React, { useEffect, useState } from 'react';
import axios from 'axios';
import WishlistButton from './WishlistButton';
import LikeDislikeButton from './LikeDislikeButton';

const HoverCard = ({ gameId }) => {
  const [game, setGame] = useState(null);
  const [isHovered, setIsHovered] = useState(false);
  const [loading, setLoading] = useState(false);
  const fallbackImage = "/StockGameCover.jpg";

  // Function to convert YouTube URL to embed URL
  const getEmbedUrl = (url) => {
    if (!url) return null;
    
    // Handle different YouTube URL formats
    const patterns = {
      // Regular YouTube URL
      regular: /(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\n?#]+)/,
      // YouTube embed URL
      embed: /youtube\.com\/embed\/([^&\n?#]+)/,
      // YouTube short URL
      short: /youtu\.be\/([^&\n?#]+)/
    };

    // Check if it's already an embed URL
    if (url.includes('youtube.com/embed/')) {
      return url;
    }

    // Try to extract video ID from different URL formats
    for (const pattern of Object.values(patterns)) {
      const match = url.match(pattern);
      if (match && match[1]) {
        return `https://www.youtube.com/embed/${match[1]}`;
      }
    }

    return null;
  };

  useEffect(() => {
    let timeoutId;
    
    if (isHovered && !game && !loading) {
      timeoutId = setTimeout(() => {
        fetchGameDetails();
      }, 300);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isHovered, game, loading]);

  const fetchGameDetails = async () => {
    try {
      setLoading(true);
      const response = await axios.post('/api/query', {
        mainTable: 'games g',
        fields: [
          'g.id',
          'g.name',
          'g.first_release_date',
          'g.coverUrl',
          'g.trailerUrl',
          'g.storyline',
          'GROUP_CONCAT(DISTINCT gen.name) as genres',
          'GROUP_CONCAT(DISTINCT t.name) as themes',
          'GROUP_CONCAT(DISTINCT ar.rating) as age_ratings',
          'GROUP_CONCAT(DISTINCT ls.language) as language_supports'
        ],
        joins: [
          {
            type: 'LEFT',
            table: 'game_genres gg',
            on: 'g.id = gg.game_id'
          },
          {
            type: 'LEFT',
            table: 'genres gen',
            on: 'gg.genre_id = gen.id'
          },
          {
            type: 'LEFT',
            table: 'game_themes gt',
            on: 'g.id = gt.game_id'
          },
          {
            type: 'LEFT',
            table: 'themes t',
            on: 'gt.theme_id = t.id'
          },
          {
            type: 'LEFT',
            table: 'game_age_ratings gar',
            on: 'g.id = gar.game_id'
          },
          {
            type: 'LEFT',
            table: 'age_ratings ar',
            on: 'gar.age_rating_id = ar.id'
          },
          {
            type: 'LEFT',
            table: 'game_language_supports gls',
            on: 'g.id = gls.game_id'
          },
          {
            type: 'LEFT',
            table: 'language_supports ls',
            on: 'gls.language_support_id = ls.id'
          }
        ],
        conditions: ['g.id = ?'],
        groupBy: ['g.id'],
        params: [gameId]
      });
      
      if (response.data && response.data.length > 0) {
        const gameData = response.data[0];
        setGame({
          id: gameData.id,
          name: gameData.name,
          releaseDate: new Date(gameData.first_release_date * 1000).toISOString().slice(0, 10),
          releaseDateUnix: gameData.first_release_date,
          coverUrl: gameData.coverUrl || fallbackImage,
          trailerUrl: getEmbedUrl(gameData.trailerUrl),
          storyline: gameData.storyline || 'No summary available',
          genres: gameData.genres ? gameData.genres.split(',') : [],
          themes: gameData.themes ? gameData.themes.split(',') : [],
          ageRatings: gameData.age_ratings ? gameData.age_ratings.split(',') : [],
          languageSupports: gameData.language_supports ? gameData.language_supports.split(',') : []
        });
      }
    } catch (error) {
      console.error('Error fetching game details:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="absolute top-1/2 left-0 transform -translate-y-1/2 z-30 w-full max-w-[350px] bg-white p-6 rounded-xl shadow-2xl scale-0 group-hover:scale-100 transition-transform duration-300 origin-left text-gray-900"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {loading ? (
        <div className="w-full h-56 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      ) : (
        <>
          {isHovered && game?.trailerUrl ? (
            <iframe
              className="w-full h-56 mb-4 rounded"
              src={game.trailerUrl}
              title={`${game.name} Trailer`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              aria-label={`${game.name} Trailer`}
            />
          ) : (
            <img
              src={game?.coverUrl || fallbackImage}
              alt={game?.name || 'Game Cover'}
              className="w-full h-56 object-cover rounded mb-4"
              onError={(e) => { e.target.src = fallbackImage; }}
            />
          )}

          <h3 className="font-bold text-xl mb-1 text-center">{game?.name || 'Loading...'}</h3>
          <p className="text-sm text-gray-600 mb-2 text-center">
            Releases on {game?.releaseDate || 'TBA'}
          </p>
          <p className="text-sm text-gray-700 mb-4">{game?.storyline || 'No summary available'}</p>

          <div className="flex items-center justify-center gap-4">
            <WishlistButton
              gameId={gameId}
              releaseDate={game?.first_release_date}
            />
            <LikeDislikeButton gameId={gameId} />
          </div>
        </>
      )}
    </div>
  );
};

export default HoverCard;
